/**
 * Runtime validation of Jeabs responses.
 *
 * RULE: every optional field is ALSO nullable. The upstream sends null for
 * absent data as readily as it omits the key, and the two are not
 * distinguishable in advance -- heroes, stats, extras, searched_at and
 * fetched_at were each discovered to be nullable by a sync failing in
 * production, one field at a time. Encoding the rule once is cheaper than
 * rediscovering it per outage, and costs nothing: a null we accept is
 * normalized to null downstream either way.
 *
 * The governing constraint (spec sections 1.3 and 74) is that we must NEVER
 * discard unknown fields. So every object schema here uses `.passthrough()`
 * and every field the spec does not guarantee is `.optional()`.
 *
 * These schemas exist to catch *shape* breakage -- "players suddenly returns an
 * array" -- not to filter the payload. The untouched body is persisted to jsonb
 * before validation runs, so even a validation failure preserves the data.
 */

import { z } from 'zod';

/** Jeabs is inconsistent about numeric vs string IDs across routes. */
const idLike = z.union([z.string(), z.number()]).transform((v) => String(v));

/** Tolerates a numeric field arriving as a numeric string. */
const numberLike = z.union([z.number(), z.string()]).transform((v) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
});

export const jeabsAllianceRefSchema = z
  .object({
    id: numberLike,
    abbr: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
  })
  .passthrough();

export const jeabsPlayerSchema = z
  .object({
    id: idLike,
    name: z.string(),
    avatar_url: z.string().nullable().optional().nullable(),
    kingdom: numberLike.nullable().optional(),
    town_hall_level: numberLike.nullable().optional(),
    power: numberLike.nullable().optional(),
    level: numberLike.nullable().optional(),
    vip: numberLike.nullable().optional(),
    kills: numberLike.nullable().optional(),
    mystic_trial_score: numberLike.nullable().optional(),
    alliance: jeabsAllianceRefSchema.nullable().optional(),
    active: z.boolean().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    hero_gear: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough();

export const jeabsStandingRowSchema = z
  .object({
    board_type: numberLike.nullable().optional(),
    type: numberLike.nullable().optional(),
    label: z.string().nullable().optional(),
    /**
     * Live responses use `kingdom`; `kingdom_id` is the spelling in the spec
     * text. Both accepted -- read via `standingKingdomOf()`.
     */
    kingdom: numberLike.nullable().optional(),
    kingdom_id: numberLike.nullable().optional(),
    rank: numberLike.nullable().optional(),
    score: numberLike.nullable().optional(),
    points: numberLike.nullable().optional(),
    global_rank: numberLike.nullable().optional(),
    global_total: numberLike.nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .passthrough();

/** Reads the board's kingdom regardless of which spelling the upstream used. */
export function standingKingdomOf(row: {
  kingdom?: number | null | undefined;
  kingdom_id?: number | null | undefined;
}): number | null {
  return row.kingdom ?? row.kingdom_id ?? null;
}

/** Verified 2026-08-24: `{player_id, name, kingdom, standings, searched_at}`. */
export const jeabsPlayerStandingsSchema = z
  .object({
    player_id: idLike.nullable().optional(),
    name: z.string().nullable().optional(),
    kingdom: numberLike.nullable().optional(),
    standings: z.array(jeabsStandingRowSchema).nullable().optional(),
    /** Verified null in the wild -- do not assume a timestamp is present. */
    searched_at: z.string().nullable().optional(),
  })
  .passthrough();

export const jeabsHeroSkillSchema = z
  .object({ id: numberLike, level: numberLike.nullable().optional() })
  .passthrough();

export const jeabsHeroEquipmentSchema = z
  .object({
    eid: numberLike,
    slv: numberLike.nullable().optional(),
    exp: numberLike.nullable().optional(),
    rlv: numberLike.nullable().optional(),
    /**
     * Required. Spec section 8.4 makes slot identity depend entirely on `sid`,
     * so a piece without one cannot be placed and must not silently fall back
     * to its array index.
     */
    sid: numberLike,
  })
  .passthrough();

export const jeabsHeroSchema = z
  .object({
    slot: numberLike.nullable().optional(),
    id: numberLike,
    lv: numberLike.nullable().optional(),
    star: numberLike.nullable().optional(),
    pos: numberLike.nullable().optional(),
    skills: z.array(jeabsHeroSkillSchema).nullable().optional(),
    exclusive_equip: numberLike.nullable().optional(),
    exclusive_equip_lv: numberLike.nullable().optional(),
    equipment: z.array(jeabsHeroEquipmentSchema).nullable().optional(),
  })
  .passthrough();

export const jeabsHeroGearSchema = z
  .object({
    player_id: idLike,
    heroes: z.array(jeabsHeroSchema).nullable().optional(),
    extras: z.record(z.unknown()).nullable().optional(),
    /** Left as an opaque key/number map. Meanings live in opaque_stat_catalog. */
    stats: z.record(numberLike).nullable().optional(),
    fetched_at: z.string().nullable().optional(),
    stale: z.boolean().nullable().optional(),
  })
  .passthrough();

export const jeabsAllianceMemberSchema = z
  .object({
    id: idLike.nullable().optional(),
    governor_id: idLike.nullable().optional(),
    name: z.string().nullable().optional(),
    rank: numberLike.nullable().optional(),
    power: numberLike.nullable().optional(),
    town_hall_level: numberLike.nullable().optional(),
  })
  .passthrough();

export const jeabsAllianceSchema = z
  .object({
    id: numberLike,
    kingdom: numberLike.nullable().optional(),
    abbr: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    level: numberLike.nullable().optional(),
    power: numberLike.nullable().optional(),
    kills: numberLike.nullable().optional(),
    member_count: numberLike.nullable().optional(),
    member_max: numberLike.nullable().optional(),
    leader: z.union([z.string(), z.record(z.unknown())]).nullable().optional().nullable(),
    members: z.array(jeabsAllianceMemberSchema).nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .passthrough();

/** One per-board summary row inside a kingdom response's `stats` array. */
export const jeabsKingdomStatSchema = z
  .object({
    key: z.string().nullable().optional(),
    label: z.string().nullable().optional(),
    board_type: numberLike.nullable().optional(),
    top: numberLike.nullable().optional(),
    total: numberLike.nullable().optional(),
    player_count: numberLike.nullable().optional(),
    /** The KINGDOM's rank among all kingdoms for this board. */
    rank: numberLike.nullable().optional(),
    out_of: numberLike.nullable().optional(),
  })
  .passthrough();

/**
 * Verified against kingdom 2393 on 2026-08-24. The live response is
 * `{alliances, kid, stats, top_players, tracked_players}` -- note `kid`, not
 * `id`, and `stats`, not the `stat_rankings` the spec text suggested.
 *
 * Both spellings are accepted and both are optional, because guessing wrong
 * here fails EVERY kingdom sync rather than degrading. Use `kingdomIdOf()` to
 * read the id instead of reaching for a field directly.
 */
export const jeabsKingdomSchema = z
  .object({
    id: numberLike.nullable().optional(),
    kid: numberLike.nullable().optional(),
    name: z.string().nullable().optional(),
    tracked_players: numberLike.nullable().optional(),
    alliances: z
      .array(
        // extend(), NOT .and(): an intersection re-parses both sides, and
        // passthrough returns member_ids untransformed on one side while
        // idLike stringifies it on the other, so numeric ids fail to merge
        // with invalid_intersection_types and break the whole kingdom sync.
        jeabsAllianceSchema.extend({ member_ids: z.array(idLike).nullable().optional() }),
      )
      .nullable()
      .nullable().optional(),
    top_players: z.array(jeabsPlayerSchema).nullable().optional(),
    /**
     * Verified 2026-08-24: an ARRAY of per-board summaries, e.g.
     * {key:"power", label:"Total Power", board_type:3, top:100, total:...,
     *  player_count:100, rank:2396, out_of:2443}. Not an object map.
     * `rank`/`out_of` give the kingdom's standing among all kingdoms.
     */
    stats: z.array(jeabsKingdomStatSchema).nullable().optional(),
    /** Spelling used in the spec text. Kept in case it appears on other routes. */
    stat_rankings: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough();

/** Reads the kingdom id regardless of which spelling the upstream used. */
export function kingdomIdOf(k: { id?: number | null | undefined; kid?: number | null | undefined }): number | null {
  return k.kid ?? k.id ?? null;
}

/**
 * Verified 2026-08-24 against kingdom 2393, board 3.
 *
 * The abbreviation arrives as `alliance`, NOT `alliance_abbr`. Each row also
 * carries its own `fetched_at`, which can differ from the envelope's.
 */
export const jeabsLeaderboardRowSchema = z
  .object({
    kingdom_id: numberLike.nullable().optional(),
    rank: numberLike.nullable().optional(),
    player_id: idLike.nullable().optional(),
    uid: idLike.nullable().optional(),
    name: z.string().nullable().optional(),
    /** Observed spelling for the alliance abbreviation. */
    alliance: z.string().nullable().optional(),
    /** Spec-text spelling. Read via `leaderboardAbbrOf()`. */
    alliance_abbr: z.string().nullable().optional(),
    alliance_name: z.string().nullable().optional(),
    power: numberLike.nullable().optional(),
    score: numberLike.nullable().optional(),
    fetched_at: z.string().nullable().optional(),
  })
  .passthrough();

/**
 * Verified 2026-08-24: rows arrive under `entries`, NOT `rows`, and the board
 * id is `board_type`, not `type`.
 *
 * Both spellings are accepted because the earlier schema silently parsed a
 * 100-row response into zero rows -- a clean pass that ingests nothing is far
 * more dangerous than a hard failure. Read via `leaderboardRowsOf()`.
 */
export const jeabsLeaderboardSchema = z
  .object({
    kingdom_id: numberLike.nullable().optional(),
    board_type: numberLike.nullable().optional(),
    type: numberLike.nullable().optional(),
    label: z.string().nullable().optional(),
    count: numberLike.nullable().optional(),
    entries: z.array(jeabsLeaderboardRowSchema).nullable().optional(),
    rows: z.array(jeabsLeaderboardRowSchema).nullable().optional(),
    fetched_at: z.string().nullable().optional(),
    refreshed: z.boolean().nullable().optional(),
  })
  .passthrough();

export type LeaderboardRowLike = {
  alliance?: string | null | undefined;
  alliance_abbr?: string | null | undefined;
  [key: string]: unknown;
};

export type LeaderboardLike = {
  board_type?: number | null | undefined;
  type?: number | null | undefined;
  count?: number | null | undefined;
  entries?: LeaderboardRowLike[] | null | undefined;
  rows?: LeaderboardRowLike[] | null | undefined;
  [key: string]: unknown;
};

/**
 * Structurally typed so it accepts both the parsed schema output and the
 * domain `JeabsLeaderboard` the client returns. Reading `.rows` directly
 * compiles fine and silently yields nothing, which is exactly the trap this
 * exists to close -- so it must be callable everywhere.
 */
export function leaderboardRowsOf<T>(
  lb: { entries?: T[] | null | undefined; rows?: T[] | null | undefined },
): T[] {
  return lb.entries ?? lb.rows ?? [];
}

export function leaderboardTypeOf(lb: {
  board_type?: number | null | undefined;
  type?: number | null | undefined;
}): number | null {
  return lb.board_type ?? lb.type ?? null;
}

export function leaderboardAbbrOf(row: {
  alliance?: string | null | undefined;
  alliance_abbr?: string | null | undefined;
}): string | null {
  return row.alliance ?? row.alliance_abbr ?? null;
}

/**
 * Sanity guard against the silent-empty failure above: if the envelope reports
 * a count but no rows were extracted, the shape changed again.
 */
export function leaderboardRowCountMismatch(lb: LeaderboardLike): boolean {
  const declared = lb.count ?? null;
  if (declared === null) return false;
  return declared > 0 && leaderboardRowsOf(lb).length === 0;
}

export const jeabsKvkRowSchema = z
  .object({
    event_ts: numberLike.nullable().optional(),
    event_date: z.string().nullable().optional(),
    opponent_kingdom_id: numberLike.nullable().optional(),
    decided: z.boolean().nullable().optional(),
    outcome: z.string().nullable().optional(),
    prep_winner: numberLike.nullable().optional(),
    prep_loser: numberLike.nullable().optional(),
    battle_winner: numberLike.nullable().optional(),
    battle_loser: numberLike.nullable().optional(),
    castle_taken_from: numberLike.nullable().optional(),
  })
  .passthrough();

/**
 * Verified 2026-08-24: `{kingdom_id, mode, sovereign, record, count, matches}`.
 * A kingdom with no KvK history returns count 0 and an empty matches array --
 * that is a real empty, not a parse failure.
 */
export const jeabsKvkSchema = z
  .object({
    kingdom_id: numberLike.nullable().optional(),
    mode: z.string().nullable().optional(),
    sovereign: z.boolean().nullable().optional(),
    /** Aggregate tallies. Stored as-is; we do not recompute from matches. */
    record: z
      .object({
        engagements: numberLike.nullable().optional(),
        prep_wins: numberLike.nullable().optional(),
        prep_losses: numberLike.nullable().optional(),
        battle_wins: numberLike.nullable().optional(),
        battle_losses: numberLike.nullable().optional(),
        castles_taken: numberLike.nullable().optional(),
        castles_lost: numberLike.nullable().optional(),
        opponents: numberLike.nullable().optional(),
        pending: numberLike.nullable().optional(),
      })
      .passthrough()
      .nullable()
      .nullable().optional(),
    count: numberLike.nullable().optional(),
    matches: z.array(jeabsKvkRowSchema).nullable().optional(),
  })
  .passthrough();
